import { Link, Redirect } from 'expo-router';
import { Building2Icon, ShieldIcon } from 'lucide-react-native';
import { ActivityIndicator, ScrollView, View } from 'react-native';

import { useSession } from '@/components/session-provider';
import { useTenant } from '@/components/tenant-provider';
import { Button } from '@sixam/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@sixam/ui/card';
import { Icon } from '@sixam/ui/icon';
import { Text } from '@sixam/ui/text';

export default function AdminIndexRedirect() {
  const { isAdmin } = useSession();
  const { isTenantLoading, memberships, resolveDefaultSlug, tenants } = useTenant();

  if (isTenantLoading) {
    return (
      <View className="flex-1 items-center justify-center bg-background">
        <ActivityIndicator />
      </View>
    );
  }

  const adminTenants = tenants.filter((tenant) => {
    if (isAdmin) {
      return true;
    }
    const role = memberships.find((membership) => membership.tenant_id === tenant.id)?.role;
    return role === 'admin' || role === 'owner';
  });
  const slug = adminTenants[0]?.slug;
  if (!slug) {
    const memberSlug = resolveDefaultSlug();
    if (memberSlug) {
      return <Redirect href="/app" />;
    }

    return (
      <View className="flex-1 items-center justify-center bg-background p-6">
        <Text variant="muted">No clubs are available for your account yet.</Text>
      </View>
    );
  }

  if (adminTenants.length > 1) {
    return (
      <ScrollView
        className="flex-1 bg-background"
        contentContainerClassName="min-h-full justify-center gap-5 p-6"
      >
        <View className="mx-auto w-full max-w-2xl gap-5">
          <View className="gap-2">
            <Text variant="h1">Admin</Text>
            <Text variant="muted">Choose the club operations workspace you want to enter.</Text>
          </View>

          <View className="gap-3">
            {adminTenants.map((tenant) => {
              const role =
                memberships.find((membership) => membership.tenant_id === tenant.id)?.role ??
                (isAdmin ? 'admin' : null);

              return (
                <Card key={tenant.id}>
                  <CardHeader>
                    <View className="flex-row items-start justify-between gap-3">
                      <View className="min-w-0 flex-1 gap-1">
                        <CardTitle numberOfLines={2}>{tenant.name}</CardTitle>
                        <CardDescription numberOfLines={1}>{tenant.slug}</CardDescription>
                      </View>
                      <View className="flex-row items-center gap-1 rounded-md border border-border px-2 py-1">
                        <Icon as={ShieldIcon} className="size-3.5 text-primary" />
                        <Text variant="label" className="text-xs">
                          {role ?? 'admin'}
                        </Text>
                      </View>
                    </View>
                  </CardHeader>
                  <CardContent>
                    <Link
                      href={{
                        pathname: '/admin/[tenant]',
                        params: { tenant: tenant.slug },
                      }}
                      asChild
                    >
                      <Button className="w-full">
                        <Icon as={Building2Icon} className="size-4 text-primary-foreground" />
                        <Text>Enter</Text>
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </View>
        </View>
      </ScrollView>
    );
  }

  return <Redirect href={{ pathname: '/admin/[tenant]', params: { tenant: slug } }} />;
}
